import { Document as ContentfulDocument } from '@contentful/rich-text-types';
import {
  BenefitPlanCoverageType,
  ElectionCalculationMethod,
  ElectionProfileType,
  ElectionScheduleType,
  EntryType,
} from '@models/account/model';
import { CardPackage } from '@models/card/model';
import { BenefitPlan, PaymentPlan, PaymentPlanEligibilityEvent, PaymentPlanOverviewItem } from '@models/configuration/model';
import { BenefitAccountSummaryByBenefitPlan, ClientAccountSummary } from '@models/dashboard/model';
import { EligibilityEvent, EventSummaryStateType } from '@models/profileConfiguration/model';
import { Entry } from 'contentful';
import { BasePlanType, ErrorType, OrderDirection, SortOrderLowerCase } from './enum';
import {
  CensusConfig,
  ClaimConnexConfig,
  CrossroadsConfig,
  DependentConfig,
  EnrollmentConfig,
  EntityBase,
  EventConfig,
  FileType,
  GeneralInitialNoticeConfig,
  ProcessingType,
  PvrConfig,
} from '@models/file/model';
import { ClientCause } from '@models/giving/model';

export interface UIOption<T> {
  value: T;
  label: string;
}

export interface BrandResources {
  BASIC: BrandResource;
  TASC: BrandResource;
  Unknown: BrandResource;
}

export interface BrandResource {
  /** A brand-specific CSS class that can be applied to the HTML to help with CSS selection. Ex: 'brand-tasc', 'brand-basic' */
  brandCssClass: string;
  /** The legal text to display on the card page */
  cardLegalText: string;
  /** The label for referring to the company card. Ex: 'TASC Card', 'BASIC Card' */
  companyCard: string;
  /** The full company name. Ex: 'Total Administrative Services Corporation' */
  companyFullName: string;
  /** The short version of the company name. Ex: 'TASC', 'BASIC' */
  companyName: string;
  /** The company name to use in the copyright message. Ex: BASIC Benefits, LLC */
  companyNameCopyright: string;
  /** The hours of operation for customer support. Ex: 'Monday - Friday, 8am-5pm CT' */
  contactHours: string;
  /** This text is for contentFul Documentation ID Ex: 'documentationContentTypeBasic', 'documentationContentTypeClux' */
  documentationContentType: string;
  /** The label for the ID that uniquely identifies a Employer. Ex: 'TASC ID', 'Employer benefit ID' */
  employerId: string;
  /** The file name for the favicon. Ex: favicon-tasc.ico, favicon-basic.ico */
  faviconFileName: string;
  /** This text is for contentFul FAQ ID Ex: 'faqContentBasic', 'faqContentClux' */
  faqContentType: string;
  /** The label for the ID that uniquely identifies a participant. Ex: 'TASC ID', 'Member ID' */
  individualId: string;
  /** The file name containing the company logo. Ex: 'logo-tasc.png', 'logo-basic.png' */
  logoFileName: string;
  /** The company phone number. Ex: '800-422-4661' */
  phoneNumber: string;
  /** The name of this software product. Ex: Universal Benefit Account, Consumer Driven Accounts */
  productName: string;
  /** This is for place holder text for support page Ex: 'TASC Card', 'Employer/Individual benefit ID' */
  supportRequestPlaceholder: string;
  /** This is for place holder text for the individual's externalId Ex: BASIC -> 'Individual ID', TASC -> 'TASC ID' */
  supportRequestExternalId: string;
  /** This is the place holder text for the employer or client's externalId Ex: BASIC -> 'Employer ID', TASC -> 'Employer TASC ID' */
  supportRequestEmployerId: string;
  /** This is the file name of the terms of use */
  termsOfUseFileName: string;
}

export interface CardPackageViewModel {
  cardPackageId: string;
  cardPackage: CardPackage;
  cardImageUrl?: string;
}

export interface Classes {
  id: number;
  name: string;
}

export interface Pagination {
  take?: number;
  skip?: number;
}

export interface SortableColumn {
  id: string;
  name: string;
  sortOrder: SortOrderLowerCase;
  active: boolean;
}

export interface SearchQuery extends Pagination {
  orderBy?: string;
  orderDirection?: OrderDirection | string;
  payrollScheduleId?: string;
  effectiveDate?: string;
  searchTerm?: string;
  includeDependents?: boolean;
  effectiveStartDate?: string;
  enroller?: string;
}

export enum StepCssClass {
  Active = 'active',
  Default = 'default',
  Done = 'done',
}

export interface BankAccountSearchQuery extends SearchQuery {
  includeInactive?: boolean;
}

export interface PaymentSourceAccountSearchQuery extends SearchQuery {
  includeInactive?: boolean;
}

export interface SearchResults<T> {
  data: T;
  hasMore: boolean;
  totalCount?: number;
}

export interface PostingScheduleInfo {
  employeeDeductionSchedule: PlanSpecificPostingDates;
  employerContributionSchedule: PlanSpecificPostingDates;
}

export interface TieredElectionCalculationGetQuery extends SearchQuery {
  benefitPlanId: string;
  numberOfDependents: number;
  effectiveDate: string;
  coverageTier: BenefitPlanCoverageType;
}

export interface TieredElectionCalculationResult {
  amount: number;
  proratedAmount: number;
  prorationPercentage: number;
}

export type PlanSpecificPostingDates = Map<string, Set<string>>;

export interface EmitterMessage {
  message: string;
  type: boolean | string;
  undo?: boolean;
  link?: string;
  postManuallyLink?: boolean;
}

export interface IsElectionChangeValidQuery extends SearchQuery {
  accountId: string;
  effectiveDate: string;
  electionProfileType: ElectionProfileType;
  amount: number;
  calculationMethod: ElectionCalculationMethod;
  electionScheduleType: ElectionScheduleType
  electionSum?: number;
}

export interface IsElectionChangeValidResult {
  isElectionChangeValid: boolean;
  errorMessage: string;
  warningMessage: string;
}

export interface CalculateTotalPerPayPeriodContributionAmountQuery extends SearchQuery {
  benefitPlanId: string;
  effectiveDate: string;
  electionProfileType: ElectionProfileType;
  amount: number;
}

export interface CalculateTotalPerPayPeriodContributionAmountResult {
  totalContributionAmount: number;
}

export interface EntrySearchQuery extends SearchQuery {
  individualId?: string;
}
export interface ErrorNotification {
  status: number;
  type?: ErrorType;
}

export interface ErrorTextObj {
  required?: string;
  dateInvalid?: string;
  afterMaxDate?: string;
  beforeMinDate?: string;
  dateInvalidMin?: string;
  dateInvalidMax?: string;
  [key: string]: string;
}

type ContentfulEntry<T> = Pick<Entry<T>, 'fields'>

export interface ContentFulEntryFaq {
  answer?: string;
  question?: string;
  keywords?: string[];
}

export interface ContentFulEntryVideo {
  videos?: { fields: ContentFulFieldItem };
  thumbnail?: { fields: ContentFulFieldItem };
  title?: string;
  description?: ContentfulDocument;
  brand: {
    fields: {
      title: string;
    },
  };
  duration: string;
  helpCategory: {
    fields: {
      title: string;
    },
  };
}

export interface ContentFulEntryDocument {
  title?: string;
  document?: { fields: ContentFulFieldItem };
  brand: {
    fields: {
      title: string;
    },
  };
  description?: ContentfulDocument;
  helpCategory: {
    fields: {
      title: string;
    },
  };
}

export interface ContentFulFileItem {
  fileName?: string;
  url?: string;
  contentType?: string;
}

export interface ContentFulFieldItem {
  title?: string;
  description?: string;
  file?: ContentFulFileItem;
}

export interface UserGuidesAndVideos extends Documentation {
  video?: MediaFile;
  userGuide?: UserGuides;
}

export interface MediaFile {
  videoUrl?: string;
  imageUrl?: string;
  duration?: string;
}

export interface UserGuides {
  url?: string;
}
export interface Documentation {
  title?: string;
  description?: string;
  helpCategory?: string;
  datePosted?: string;
  type: 'video' | 'userGuide';
}

export interface ContentfulBrand {
  title: string;
}

export interface ContentfulContextualHelpItem {
  title: string;
  name: string;
  clientDocumentation: ContentfulEntry<ContentFulEntryDocument>;
  clientTutorialVideo: ContentfulEntry<ContentFulEntryVideo>;
}

export interface ContentfulContextualHelp {
  title: string;
  url: string;
  description: ContentfulDocument | string;
  contextualHelpItems: Array<ContentfulEntry<ContentfulContextualHelpItem>>;
  brand: ContentfulEntry<ContentfulBrand>;
}

export enum ContentfulContentType {
  ContextualHelp = 'contextualHelp',
}

export enum ContentfulUrlAnchor {
  ActivePlansExpenditures = 'active-plans-expenditures',
  ActivePlansContributions = 'active-plans-contributions',
  AddEmployee = 'add-employee',
  BcsRenewalInformation = 'renewal-information',
  BcsRenewalInformationModal = 'renewal-information-modal',
  BcsRenewalOpenEnrollment = 'open-enrollment',
  BcsRenewalConfirmSubmit = 'confirm-submit',
  BcsRenewalConfirmSubmitAcknowledgement = 'confirm-submit-acknowledgement',
  BcsRenewalConfirmSubmitSuccessModal = 'confirm-submit-success-modal',
  ClientCAB = 'client-cab',
  ClientFundingRemittances = 'client-funding-remittances',
  ClientSupportRequest = 'client-support-request',
}

export interface DialogData<T> {
  hasCloseBtn?: boolean;
  data: T;
}
export interface UserGuidesAndVideos extends Documentation {
  video?: MediaFile;
  userGuide?: UserGuides;
}

export interface MediaFile {
  videoUrl?: string;
  imageUrl?: string;
  duration?: string;
}

export interface UserGuides {
  url?: string;
}
export interface Documentation {
  title?: string;
  description?: string;
  helpCategory?: string;
  datePosted?: string;
  type: 'video' | 'userGuide';
}

export enum ContentTypes {
  VIDEOS = 'videos',
  USERGUIDES = 'clientDocumentation',
}

export interface EventCategory {
  eventCategory?: string;
  eventAction?: string;
  eventLabel?: string;
  eventValue?: number;
  eventNameMapping?: {
    [key: string]: string;
  };
}

export interface TopicSubTopicInformation {
  topic?: string;
  subTopic?: string;
}

export interface SearchPayrollDatesResponse {
  client: string[];
  participant: string[];
}

export interface BenefitPlanSummaryData extends BenefitPlan {
  rollover?: boolean;
  rolloverMaxClaculation?: string;
  className?: Classes[];
  allowCarryOverCalcPercentageLimit?: boolean;
  rolloverCalcMaxPercent?: string;
  allowCarryOverCalcPercentageAndAmountLimit?: boolean;
  allowCarryOverCalcAmountLimit?: boolean;
  allowCarryoverCalcFullAvailableBalance?: boolean;
  benefitEffectiveDate?: string;
  eligibilityEndDate?: string;
  menu?: boolean;
  employeeCount?: string;
  basePlanType?: BasePlanType;
  employerElectionSchedules?: ElectionScheduleType[];
  employeeElectionSchedules?: ElectionScheduleType[];
}

export interface PaymentPlanSummaryData extends PaymentPlan {
  employeeCount?: string;
  menu?: boolean;
  basePlanType: BasePlanType;
}

export interface ClientCauseSummaryData extends ClientCause {
  balance?: number;
  menu?: boolean;
}

export interface EligibilityEventSummaryData extends EligibilityEvent {
  menu?: boolean;
  eligibilityStartDate?: string;
  eligibilityEndDate?: string;
  electionDueDate?: string;
  eventSummaryState?: EventSummaryStateType;
}

export interface BenefitPlanOverview {
  benefitPlan: BenefitPlan;
  benefitEnrollmentSummary?: BenefitAccountSummaryByBenefitPlan;
}

export interface IndividualPaymentPlanOverview {
  paymentPlanSummary: PaymentPlanOverviewItem;
}

export interface ClientAccountOverview {
  clientAccountSummary: ClientAccountSummary;
}

export interface ClientCauseViewModel {
  name: string;
  balance: number;
  transactions: {
    scheduled: TransactionsViewModel[];
    historical: TransactionsViewModel[];
  };
}

export interface TransactionsViewModel {
  date: string;
  status: string;
  amount: number;
  details?: string;
  disbursedTo: unknown;
  entryType?: EntryType;
}

export interface PaymentPlanMinimumEventDate {
  paymentPlanId: string;
  minimumQualifiedEventDate: string;
  eventTypes: PaymentPlanEligibilityEvent;
}

export interface ClaimConnexConfigViewModel extends ClaimConnexConfig {
  isDefaultConfig?: boolean;
}

export interface FileProcessConfigViewModel extends EntityBase {
  fileType: FileType;
  allowed: boolean;
  processingType?: ProcessingType;
  config?: FileProcessConfigExtraViewModel;

}

interface FileProcessConfigExtraViewModel {
  claimConnex?: ClaimConnexConfigViewModel;
  census?: CensusConfig;
  crossroads?: CrossroadsConfig;
  enrollment?: EnrollmentConfig;
  postingVerification?: PvrConfig;
  dependent?: DependentConfig;
  event?: EventConfig;
  generalInitialNotice?: GeneralInitialNoticeConfig;
}

export enum ClientCauseActions {
  AccountSummary = 'Account Summary',
}
